
import React, { useState, useEffect } from 'react';
import { workoutsAPI } from '../../api';
import { useTranslation } from '../../i18n';
import { toast } from '../common/Notifier';
import { formatPace } from '../../utils/formatPace';
import { formatDuration } from '../../utils/formatDuration';
import { format, parseISO } from 'date-fns';
import {
  computeAveragesFromRows,
  computeSums,
  computeSumsFromRows,
  groupConsecutiveByType
} from "../../pages/WorkoutDetail";

const INTERVAL_TYPES = ['all', 'steady', 'warmup', 'work', 'recovery', 'cooldown'];

function formatDistance(value) {
  if (value === null || value === undefined) return '—';
  return Math.round(value) + ' м';
}
function badgeClass(type) {
  return type === 'work' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700';
}

// Локальная копия логики computeAverages из WorkoutDetail — те же средние,
// но без каденса/stride length, так как здесь достаточно summary для сравнения
function computeAverages(intervals) {
  if (!intervals?.length) return null;

  const totalDistance = intervals.reduce((sum, i) => sum + (i.effective_distance_m || 0), 0);
  const totalDuration = intervals.reduce((sum, i) => sum + (i.duration_sec || 0), 0);
  const avgPace = totalDistance > 0 ? (totalDuration / 60) / (totalDistance / 1000) : null;

  const gapEntries = intervals.filter((i) => i.gap_min_per_km != null && i.distance_m);
  const gapDistance = gapEntries.reduce((sum, i) => sum + i.distance_m, 0);
  const avgGap = gapDistance > 0
    ? gapEntries.reduce((sum, i) => sum + i.gap_min_per_km * i.distance_m, 0) / gapDistance
    : null;

  const hrEntries = intervals.filter((i) => i.avg_heartrate != null);
  const avgHr = hrEntries.length
    ? hrEntries.reduce((sum, i) => sum + i.avg_heartrate, 0) / hrEntries.length
    : null;

  const hrMaxEntries = intervals.filter((i) => i.max_heartrate != null);
  const maxHr = hrMaxEntries.length ? Math.max(...hrMaxEntries.map((i) => i.max_heartrate)) : null;

  const cadenceEntries = intervals.filter((i) => i.avg_cadence != null && i.duration_sec);
  const cadenceDuration = cadenceEntries.reduce((sum, i) => sum + i.duration_sec, 0);
  const avgCadence = cadenceDuration > 0
    ? cadenceEntries.reduce((sum, i) => sum + i.avg_cadence * i.duration_sec, 0) / cadenceDuration
    : null;

  return { avgPace, avgGap, avgHr, maxHr, avgCadence };
}

function CompareWorkoutColumn({ workoutId, dateLabel, typeFilter, mergeMode }) {
  const { t } = useTranslation();
  const [workout, setWorkout] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await workoutsAPI.get(workoutId);
        if (!cancelled) setWorkout(data);
      } catch (e) {
        if (!cancelled) toast.error(t('error.general'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [workoutId, t]);

  if (loading) {
    return <div className="border rounded-xl p-4 text-center text-gray-400">{t('common.loading')}</div>;
  }
  if (!workout) {
    return <div className="border rounded-xl p-4 text-center text-red-400">{t('journal.compare_load_error')}</div>;
  }

  const filteredIntervals = typeFilter === 'all'
    ? workout.intervals
    : workout.intervals?.filter((i) => i.type === typeFilter);

  const displayRows = mergeMode
    ? (() => {
        const grouped = groupConsecutiveByType(workout.intervals ?? []);
        if (typeFilter === 'all') return grouped;
        return grouped.filter((row) => (row.merged ? row.type === typeFilter : row.interval.type === typeFilter));
      })()
    : null;

  const averages = mergeMode
    ? computeAveragesFromRows(displayRows ?? [])
    : computeAverages(filteredIntervals ?? []);

  const sums = mergeMode
    ? computeSumsFromRows(displayRows ?? [])
    : computeSums(filteredIntervals ?? []);

  const hasRows = mergeMode ? displayRows?.length : filteredIntervals?.length;

  return (
    <div className="border rounded-xl overflow-hidden flex flex-col">
      <div className="bg-gray-50 border-b p-3">
        <div className="font-semibold text-gray-800">{t(`${workout.workout_type}`)}</div>
        <div className="text-xs text-gray-500">
          {dateLabel || format(parseISO(workout.scheduled_date), 'dd.MM.yyyy, EEEE')}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 p-3 text-sm border-b">
        <div>
          <p className="text-gray-400 text-xs">{t('workouts.distance')}</p>
          <p className="font-semibold">{workout.actual_distance_km ?? '—'} км</p>
        </div>
        <div>
          <p className="text-gray-400 text-xs">{t('workouts.time')}</p>
          <p className="font-semibold">{formatDuration(workout.actual_duration_sec)}</p>
        </div>
        <div>
          <p className="text-gray-400 text-xs">{t('workouts.pace')}</p>
          <p className="font-semibold">{formatPace(workout.actual_avg_pace_min_per_km)} /км</p>
        </div>
        <div>
          <p className="text-gray-400 text-xs">{t('workouts.elevation')}</p>
          <p className="font-semibold">{Math.round(workout.actual_elevation_gain_m ?? 0)} м</p>
        </div>
        {averages?.avgHr != null && (
          <div>
            <p className="text-gray-400 text-xs">{t('workouts.hr_avg')}</p>
            <p className="font-semibold">{Math.round(averages.avgHr)}</p>
          </div>
        )}
        {averages?.avgCadence != null && (
          <div>
            <p className="text-gray-400 text-xs">{t('workouts.cadence')}</p>
            <p className="font-semibold">{Math.round(averages.avgCadence)}</p>
          </div>
        )}
      </div>

      {(workout.rpe != null || workout.comment) && (
        <div className="p-3 border-b text-xs text-gray-500 space-y-1">
          {workout.rpe != null && <div>RPE: {workout.rpe}</div>}
          {workout.comment && <div className="italic">💬 {workout.comment}</div>}
        </div>
      )}

      <div className="overflow-auto flex-1">
        {hasRows ? (
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-400 sticky top-0">
              <tr>
                <th className="py-1.5 px-2 text-left">#</th>
                <th className="py-1.5 px-2 text-left">{t('workouts.type')}</th>
                <th className="py-1.5 px-2 text-right">{t('workouts.distance')}</th>
                <th className="py-1.5 px-2 text-right">{t('workouts.time')}</th>
                <th className="py-1.5 px-2 text-right">{t('workouts.pace')}</th>
                <th className="py-1.5 px-2 text-right">GAP</th>
                <th className="py-1.5 px-2 text-right">{t('workouts.hr_avg')}</th>
                <th className="py-1.5 px-2 text-right">{t('workouts.cadence')}</th>
              </tr>
            </thead>
            <tbody>
              {mergeMode
                ? displayRows.map((row) =>
                    row.merged ? (
                      <tr key={row.key} className="border-t last:border-0 bg-purple-50/40">
                        <td className="py-1.5 px-2 text-gray-400">{row.indexRange}</td>
                        <td className="py-1.5 px-2">
                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${badgeClass(row.type)}`}>
                            {t(`workouts.${row.type}`)}
                          </span>
                        </td>
                        <td className="py-1.5 px-2 text-right">{formatDistance(row.totalDistance)}</td>
                        <td className="py-1.5 px-2 text-right">{formatDuration(row.totalDuration)}</td>
                        <td className="py-1.5 px-2 text-right">{formatPace(row.avgPace)}</td>
                        <td className="py-1.5 px-2 text-right">{formatPace(row.avgGap)}</td>
                        <td className="py-1.5 px-2 text-right">
                          {row.avgHr != null ? Math.round(row.avgHr) : '—'}
                        </td>
                        <td className="py-1.5 px-2 text-right">
                          {row.avgCadence != null ? Math.round(row.avgCadence) : '—'}
                        </td>
                      </tr>
                    ) : (
                      <tr key={row.interval.id} className="border-t last:border-0">
                        <td className="py-1.5 px-2 text-gray-400">{row.interval.index + 1}</td>
                        <td className="py-1.5 px-2">
                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${badgeClass(row.interval.type)}`}>
                            {t(`workouts.${row.interval.type}`)}
                          </span>
                        </td>
                        <td className="py-1.5 px-2 text-right">{formatDistance(row.interval.effective_distance_m)}</td>
                        <td className="py-1.5 px-2 text-right">{formatDuration(row.interval.duration_sec)}</td>
                        <td className="py-1.5 px-2 text-right">{formatPace(row.interval.avg_pace_min_per_km)}</td>
                        <td className="py-1.5 px-2 text-right">{formatPace(row.interval.gap_min_per_km)}</td>
                        <td className="py-1.5 px-2 text-right">
                          {row.interval.avg_heartrate != null ? Math.round(row.interval.avg_heartrate) : '—'}
                        </td>
                        <td className="py-1.5 px-2 text-right">
                          {row.interval.avg_cadence != null ? Math.round(row.interval.avg_cadence) : '—'}
                        </td>
                      </tr>
                    )
                  )
                : filteredIntervals.map((interval) => (
                    <tr key={interval.id} className="border-t last:border-0">
                      <td className="py-1.5 px-2 text-gray-400">{interval.index + 1}</td>
                      <td className="py-1.5 px-2">
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${badgeClass(interval.type)}`}>
                          {t(`workouts.${interval.type}`)}
                        </span>
                      </td>
                      <td className="py-1.5 px-2 text-right">{formatDistance(interval.effective_distance_m)}</td>
                      <td className="py-1.5 px-2 text-right">{formatDuration(interval.duration_sec)}</td>
                      <td className="py-1.5 px-2 text-right">{formatPace(interval.avg_pace_min_per_km)}</td>
                      <td className="py-1.5 px-2 text-right">{formatPace(interval.gap_min_per_km)}</td>
                      <td className="py-1.5 px-2 text-right">
                        {interval.avg_heartrate != null ? Math.round(interval.avg_heartrate) : '—'}
                      </td>
                      <td className="py-1.5 px-2 text-right">
                        {interval.avg_cadence != null ? Math.round(interval.avg_cadence) : '—'}
                      </td>
                    </tr>
                  ))}
            </tbody>
            {averages && (
              <tfoot>
                <tr className="border-t-2 font-semibold text-gray-700 bg-gray-50">
                  <td className="py-1.5 px-2"></td>
                  <td className="py-1.5 px-2 text-[10px] text-gray-500">{t('workouts.average')}</td>
                  <td className="py-1.5 px-2 text-right">{Math.round(averages.avgDistance)} м</td>
                  <td className="py-1.5 px-2 text-right">—</td>
                  <td className="py-1.5 px-2 text-right">{formatPace(averages.avgPace)}</td>
                  <td className="py-1.5 px-2 text-right">{formatPace(averages.avgGap)}</td>
                  <td className="py-1.5 px-2 text-right">{averages.avgHr != null ? Math.round(averages.avgHr) : '—'}</td>
                  <td className="py-1.5 px-2 text-right">{averages.avgCadence != null ? Math.round(averages.avgCadence) : '—'}</td>
                </tr>
                {sums && (
                  <tr className="font-semibold text-gray-700 bg-gray-50">
                    <td className="py-1.5 px-2"></td>
                    <td className="py-1.5 px-2 text-[10px] text-gray-500">{t('workouts.sum')}</td>
                    <td className="py-1.5 px-2 text-right">{formatDistance(sums.totalDistance)}</td>
                    <td className="py-1.5 px-2 text-right">{formatDuration(sums.totalDuration)}</td>
                    <td className="py-1.5 px-2 text-right">—</td>
                    <td className="py-1.5 px-2 text-right">—</td>
                    <td className="py-1.5 px-2 text-right">—</td>
                    <td className="py-1.5 px-2 text-right">—</td>
                  </tr>
                )}
              </tfoot>
            )}
          </table>
        ) : (
          <p className="text-xs text-gray-400 text-center py-4">{t('workouts.no_intervals')}</p>
        )}
      </div>
    </div>
  );
}

export function CompareWorkoutsModal({ dates, onClose }) {
  const { t } = useTranslation();
  const [workoutIds, setWorkoutIds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [mergeMode, setMergeMode] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const resolveIds = async () => {
      setLoading(true);
      setError(false);
      try {
        const results = await Promise.all(
          dates.map((date) => workoutsAPI.list({ scheduled_date: date }))
        );
        const resolved = results.map((r, i) => {
          const data = r.data;
          const found = Array.isArray(data) ? data[0] : data?.results?.[0];
          return found ? { id: found.id, date: dates[i] } : null;
        });
        if (resolved.some((r) => !r)) {
          setError(true);
        } else if (!cancelled) {
          setWorkoutIds(resolved);
        }
      } catch (e) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    resolveIds();
    return () => { cancelled = true; };
  }, [dates]);

  const colsClass = dates.length === 2 ? 'grid-cols-2' : 'grid-cols-3';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-7xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b shrink-0 gap-2 flex-wrap">
          <h2 className="text-lg font-bold">{t('journal.compare_title')}</h2>

          <div className="flex gap-1 items-center ml-auto">
            <button
              onClick={() => setMergeMode((prev) => !prev)}
              className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors mr-2 ${
                mergeMode
                  ? 'bg-purple-600 text-white'
                  : 'bg-purple-50 text-purple-600 hover:bg-purple-100'
              }`}
              title={t('workouts.intervals_plus_hint')}
            >
              Intervals++
            </button>

            {INTERVAL_TYPES.map((type) => (
              <button
                key={type}
                onClick={() => setTypeFilter(type)}
                className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${
                  typeFilter === type
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {type === 'all' ? t('workouts.all_types') : t(`workouts.${type}`)}
              </button>
            ))}
          </div>

          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>

        <div className="overflow-auto p-4 flex-1">
          {loading ? (
            <div className="text-center text-gray-400 py-8">{t('common.loading')}</div>
          ) : error ? (
            <div className="text-center text-red-500 py-8">{t('journal.compare_load_error')}</div>
          ) : (
            <div className={`grid gap-4 ${colsClass}`} style={{ minHeight: '60vh' }}>
              {workoutIds.map((w) => (
                <CompareWorkoutColumn
                  key={w.id}
                  workoutId={w.id}
                  dateLabel={format(parseISO(w.date), 'dd.MM.yyyy, EEEE')}
                  typeFilter={typeFilter}
                  mergeMode={mergeMode}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}